import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { HardcodedBlock as HardcodedBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FinancingModalTriggerHomepage } from "../../apps/checkout/FinancingModalTriggerHomepage";
import { VERSION_COMPONENTS } from "../../blocks/HardcodedBlock/index";

/**
 * Dispatcher for the tempurpedic-local HardcodedBlock (#33285 / #33404).
 *
 * The block's content is a hand-maintained per-version partial. This component
 * rebuilds the legacy ``<section class="hc-block ...">`` wrapper from the typed
 * fields (same class order: version, responsive visibility, spacing) and then
 * dispatches the inner content per ``version``:
 *
 *  - ``hc-block--prequal-card``: the INTERACTIVE
 *    ``FinancingModalTriggerHomepage`` island (Redux-connected pre-qual /
 *    apply-now modal buttons), gated server-side on
 *    ``PreQualificationSettings.toolbox`` (``prequal_toolbox_enabled``). It must
 *    be a real hydration island, so it is rendered directly as a child here (NOT
 *    baked into ``html``, which would render but never hydrate -> dead buttons),
 *    threading ``application_source`` (legacy ``page.slug``). LEFT UNCHANGED.
 *
 *  - any version in ``VERSION_COMPONENTS``: rendered NATIVELY in React (#33404
 *    -- ALL non-prequal partials, including the compare tables, are now real
 *    React; the legacy Django partials they replaced have been deleted and the
 *    server no longer renders any ``html`` string).
 *
 *  - any other (genuinely-unknown) version: renders ``null``, matching the
 *    legacy ``{% file_exists %}`` else-branch, which emitted only an HTML
 *    comment.
 *
 * Spacing uses ``getGlobalSpacingClasses`` (the GLOBAL class strings emitted by
 * ``spacing-sub-block.html``), matching the legacy server render's global SCSS.
 */
export const HardcodedBlock = (
    props: Immutable<{
        value: HardcodedBlockValue;
        application_source: string;
    }>,
): React.JSX.Element | null => {
    const v = props.value;
    const version = v.version ?? "";
    const className = concatClassNames([
        "hc-block",
        version,
        v.responsive_visibility?.visibility ?? "",
        getGlobalSpacingClasses(v.spacing),
    ]);
    if (version === "hc-block--prequal-card") {
        return (
            <section className={className}>
                {v.prequal_toolbox_enabled ? (
                    // SSR boundary <div> (mirrors FinancingModalTriggerHomepageTemplate):
                    // required so useAppSelector reads fresh store state during SSR.
                    <div>
                        <FinancingModalTriggerHomepage
                            applicationSource={props.application_source}
                        />
                    </div>
                ) : null}
            </section>
        );
    }
    const VersionComponent = VERSION_COMPONENTS[version];
    if (VersionComponent) {
        return (
            <section className={className}>
                <VersionComponent />
            </section>
        );
    }
    // Genuinely-unknown version: render nothing (the legacy ``{% file_exists %}``
    // else-branch emitted only an HTML comment, i.e. no visible output).
    return null;
};
